import { useRequest } from 'ahooks';
import {
  apiV2EnvMonitorDashboardOverviewPost,
  type V2EnvMonitorDashboardOverviewPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import type { DashboardDimension } from '../types';

export interface OverviewData {
  gas?: {
    gasEmission?: number;
    gasEmissionUnit?: string;
    nox?: number;
    noxUnit?: string;
    nmhc?: number;
    nmhcUnit?: string;
  };
  water?: {
    nh3nEmission?: number;
    nh3nUnit?: string;
    tpEmission?: number;
    tpUnit?: string;
    codEmission?: number;
    codUnit?: string;
    tnEmission?: number;
    tnUnit?: string;
    waterEmission?: number;
    waterEmissionUnit?: string;
  };
}

const transformData = (res: V2EnvMonitorDashboardOverviewPostResponse | undefined): OverviewData | undefined => {
  if (!res) return undefined;

  return {
    gas: {
      gasEmission: res.exhaustIndicators?.exhaustVolume?.value,
      gasEmissionUnit: res.exhaustIndicators?.exhaustVolume?.unit,
      nox: res.exhaustIndicators?.noxEmission?.value,
      noxUnit: res.exhaustIndicators?.noxEmission?.unit,
      nmhc: res.exhaustIndicators?.nmhcEmission?.value,
      nmhcUnit: res.exhaustIndicators?.nmhcEmission?.unit,
    },
    water: {
      nh3nEmission: res.wastewaterIndicators?.ammoniaNitrogenEmission?.value,
      nh3nUnit: res.wastewaterIndicators?.ammoniaNitrogenEmission?.unit,
      tpEmission: res.wastewaterIndicators?.totalPhosphorusEmission?.value,
      tpUnit: res.wastewaterIndicators?.totalPhosphorusEmission?.unit,
      codEmission: res.wastewaterIndicators?.codEmission?.value,
      codUnit: res.wastewaterIndicators?.codEmission?.unit,
      tnEmission: res.wastewaterIndicators?.totalNitrogenEmission?.value,
      tnUnit: res.wastewaterIndicators?.totalNitrogenEmission?.unit,
      waterEmission: res.wastewaterIndicators?.wastewaterVolume?.value,
      waterEmissionUnit: res.wastewaterIndicators?.wastewaterVolume?.unit,
    },
  };
};

export const useOverviewData = (dimension: DashboardDimension, timeValue: string, refreshKey: number) => {
  const granularity = dimension === 'MONTH' ? 'M' : 'Y';

  // 根据维度计算时间范围
  const getTimeRange = () => {
    if (dimension === 'MONTH') {
      // 月维度：该月的起始和结束时间戳
      const start = dayjs(timeValue).startOf('month');
      const end = dayjs(timeValue).endOf('month');
      return {
        startTime: start.valueOf(),
        endTime: end.valueOf(),
      };
    } else {
      // 年维度：该年的起始和结束时间戳
      const start = dayjs(timeValue).startOf('year');
      const end = dayjs(timeValue).endOf('year');
      return {
        startTime: start.valueOf(),
        endTime: end.valueOf(),
      };
    }
  };

  const { startTime, endTime } = getTimeRange();

  return useRequest(
    async () => {
      const res = await apiV2EnvMonitorDashboardOverviewPost({
        granularity,
        startTime,
        endTime,
      });
      return transformData(res);
    },
    {
      ready: !!dimension && !!timeValue,
      refreshDeps: [dimension, timeValue, refreshKey],
    }
  );
};
